/* eslint-disable max-lines */
/* eslint-disable complexity */
import Link from 'next/link';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';

import { BlockErrorMessage } from '../../../Block/BlockErrorMessage';
import { ButtonBase } from '../../../Button/ButtonBase';
import { InnerButtonAreaTwoColumns } from '../../../Layout/InnerLayout/InnerButtonAreaTwoColumns';
import { Loading } from '../../../Loading';

import { AcademicBackgroundTabPanel } from './AcademicBackgroundTabPanel';
import { BasicInfoTabPanel } from './BasicInfoTabPanel';
import { QualificationsAndWorkTabPanel } from './QualificationsAndWorkTabPanel';
import { AcademicBackgroundUpdaters } from './updateAcademicBackground';
import { QualificationsAndWorkUpdaters } from './updateQualificationsAndWork';
import {
  InitAcademicBackground,
  InitBasicInfo,
  InitQualificationsAndWork,
  useProfileSettingForm,
  type Validators,
} from './useProfileSettingForm';

import type { FetchAddressByPostalCode } from './address';
import type { BasicInfoUpdaters } from './updateBasicInfo';
import type { CheckboxAttribute } from '../../../../types/CheckboxAttribute';
import type { LoggedInUser } from '../../../../types/LoggedInUser';
import type { NamePlaceholders } from '../../../../types/NamePlaceholders';
import type { SelectAttribute } from '../../../../types/SelectAttribute';
import type { FC } from 'react';

export type ProfileSettingContentsProps = {
  loggedInUser: LoggedInUser;
  qualificationAttributes: CheckboxAttribute[];
  employmentAttributes: SelectAttribute[];
  affiliationAttributes: CheckboxAttribute[];
  activityAreaAttributes: CheckboxAttribute[];
  licenseIdAttributes: CheckboxAttribute[];
  schoolTypeAttributes: SelectAttribute[];
  graduationYearAttributes: SelectAttribute[];
  namePlaceholders: NamePlaceholders;
  kanaPlaceholders: NamePlaceholders;
  validators: Validators;
  updaters: {
    basicInfo: BasicInfoUpdaters;
    qualificationsAndWork: QualificationsAndWorkUpdaters;
    academicBackground: AcademicBackgroundUpdaters;
  };
  fetchAddressByPostalCode?: FetchAddressByPostalCode;
  initBasicInfo?: InitBasicInfo;
  initQualificationsAndWork?: InitQualificationsAndWork;
  initAcademicBackground?: InitAcademicBackground;
};

// eslint-disable-next-line max-lines-per-function, max-statements
export const ProfileSettingContents: FC<ProfileSettingContentsProps> = ({
  loggedInUser,
  qualificationAttributes,
  employmentAttributes,
  affiliationAttributes,
  activityAreaAttributes,
  licenseIdAttributes,
  schoolTypeAttributes,
  graduationYearAttributes,
  namePlaceholders,
  kanaPlaceholders,
  validators,
  updaters,
  fetchAddressByPostalCode,
  initBasicInfo,
  initQualificationsAndWork,
  initAcademicBackground,
}) => {
  const {
    selectedBirthYear,
    selectedBirthMonth,
    selectedBirthday,
    selectedPrefectureId,
    locality,
    selectedMarriageStatusId,
    selectedHasChildrenId,
    birthYearAttributes,
    birthMonthAttributes,
    birthdayAttributes,
    basicInfoInvalidErrors,
    qualificationsAndWorkInvalidErrors,
    loading,
    errorMessage,
    imagePreviewUrl,
    selfIntroduction,
    qualificationCodeList,
    selectedEmploymentId,
    affiliationIds,
    activityAreaIds,
    licenseIds,
    customLicense,
    nurseLicenseNumber,
    schoolName,
    schoolTypeId,
    graduationYear,
    scholarshipStatus,
    scholarshipType,
    borrowingPeriod,
    academicBackgroundInvalidErrors,
    handleFileUpload,
    onSelectTab,
    onChangeFamilyName,
    onChangeGivenName,
    onChangeFamilyNameKana,
    onChangeGivenNameKana,
    onChangePreferredUsername,
    onChangeGender,
    onChangeBirthYear,
    onChangeBirthMonth,
    onChangeBirthday,
    onChangePhoneNumber,
    onChangePostalCode,
    onChangePrefectureId,
    onChangeLocality,
    onChangeStreetAddress,
    onChangeBuilding,
    handlePostalCodeFocusEvent,
    onChangeMarriageStatusId,
    onChangeHasChildrenId,
    onChangeSelfIntroduction,
    onChangeQualificationCodeList,
    onChangeEmploymentId,
    onChangeAffiliationIds,
    onChangeActivityAreaIds,
    onChangeLicenseIds,
    onChangeCustomLicense,
    onChangeNurseLicenseNumber,
    onChangeSchoolName,
    onChangeSchoolType,
    onChangeGraduationYear,
    onChangeScholarshipStatus,
    onChangeScholarshipType,
    onChangeBorrowingPeriod,
    submit,
    nameErrorMessage,
    kanaErrorMessage,
    profileImageMessage,
  } = useProfileSettingForm({
    validators,
    updaters,
    loggedInUser,
    initBasicInfo,
    initQualificationsAndWork,
    initAcademicBackground,
    fetchAddressByPostalCode,
  });

  return (
    <form method="post" onSubmit={submit}>
      <Tabs forceRenderTabPanel={true} onSelect={onSelectTab}>
        <TabList>
          <Tab>基本情報</Tab>
          <Tab>資格・仕事</Tab>
          <Tab>学歴</Tab>
        </TabList>

        <TabPanel>
          <BasicInfoTabPanel
            loggedInUser={loggedInUser}
            onChangePreferredUsername={onChangePreferredUsername}
            onChangeFamilyName={onChangeFamilyName}
            onChangeGivenName={onChangeGivenName}
            onChangeFamilyNameKana={onChangeFamilyNameKana}
            onChangeGivenNameKana={onChangeGivenNameKana}
            onChangeGender={onChangeGender}
            onChangeBirthYear={onChangeBirthYear}
            onChangeBirthMonth={onChangeBirthMonth}
            onChangeBirthday={onChangeBirthday}
            onChangePhoneNumber={onChangePhoneNumber}
            onChangePostalCode={onChangePostalCode}
            handlePostalCodeFocusEvent={handlePostalCodeFocusEvent}
            onChangePrefectureId={onChangePrefectureId}
            onChangeLocality={onChangeLocality}
            onChangeStreetAddress={onChangeStreetAddress}
            onChangeBuilding={onChangeBuilding}
            onChangeMarriageStatusId={onChangeMarriageStatusId}
            onChangeHasChildrenId={onChangeHasChildrenId}
            handleFileUpload={handleFileUpload}
            onChangeSelfIntroduction={onChangeSelfIntroduction}
            locality={locality}
            nameErrorMessage={nameErrorMessage}
            kanaErrorMessage={kanaErrorMessage}
            namePlaceholders={namePlaceholders}
            kanaPlaceholders={kanaPlaceholders}
            birthYearAttributes={birthYearAttributes}
            birthMonthAttributes={birthMonthAttributes}
            birthdayAttributes={birthdayAttributes}
            selectedBirthYear={selectedBirthYear}
            selectedBirthMonth={selectedBirthMonth}
            selectedBirthday={selectedBirthday}
            selectedPrefectureId={selectedPrefectureId}
            selectedMarriageStatusId={selectedMarriageStatusId}
            selectedHasChildrenId={selectedHasChildrenId}
            imagePreviewUrl={imagePreviewUrl}
            initBasicInfo={initBasicInfo}
            invalidErrors={basicInfoInvalidErrors}
            profileImageMessage={profileImageMessage}
            selfIntroduction={selfIntroduction}
          />
        </TabPanel>
        <TabPanel>
          <QualificationsAndWorkTabPanel
            qualificationAttributes={qualificationAttributes}
            employmentAttributes={employmentAttributes}
            affiliationAttributes={affiliationAttributes}
            activityAreaAttributes={activityAreaAttributes}
            licenseIdAttributes={licenseIdAttributes}
            onChangeQualificationCodeList={onChangeQualificationCodeList}
            onChangeEmploymentId={onChangeEmploymentId}
            onChangeAffiliationIds={onChangeAffiliationIds}
            onChangeActivityAreaIds={onChangeActivityAreaIds}
            onChangeLicenseIds={onChangeLicenseIds}
            onChangeCustomLicense={onChangeCustomLicense}
            onChangeNurseLicenseNumber={onChangeNurseLicenseNumber}
            invalidErrors={qualificationsAndWorkInvalidErrors}
            qualificationCodeList={qualificationCodeList}
            selectedEmploymentId={selectedEmploymentId}
            affiliationIds={affiliationIds}
            activityAreaIds={activityAreaIds}
            licenseIds={licenseIds}
            customLicense={customLicense}
            nurseLicenseNumber={nurseLicenseNumber}
          />
        </TabPanel>
        <TabPanel>
          <AcademicBackgroundTabPanel
            schoolTypeAttributes={schoolTypeAttributes}
            graduationYearAttributes={graduationYearAttributes}
            invalidErrors={academicBackgroundInvalidErrors}
            schoolName={schoolName}
            schoolTypeId={schoolTypeId}
            graduationYear={graduationYear}
            scholarshipStatus={scholarshipStatus}
            scholarshipType={scholarshipType}
            borrowingPeriod={borrowingPeriod}
            onChangeSchoolName={onChangeSchoolName}
            onChangeSchoolType={onChangeSchoolType}
            onChangeGraduationYear={onChangeGraduationYear}
            onChangeScholarshipStatus={onChangeScholarshipStatus}
            onChangeScholarshipType={onChangeScholarshipType}
            onChangeBorrowingPeriod={onChangeBorrowingPeriod}
          />
        </TabPanel>
      </Tabs>
      {errorMessage ? (
        <BlockErrorMessage>{errorMessage}</BlockErrorMessage>
      ) : (
        ''
      )}
      <InnerButtonAreaTwoColumns>
        <Link href="/" prefetch={false} legacyBehavior={true}>
          <ButtonBase color="secondary" width="wide">
            マイページに戻る
          </ButtonBase>
        </Link>
        <ButtonBase color="primary" width="wide">
          変更内容を保存
        </ButtonBase>
      </InnerButtonAreaTwoColumns>
      <Loading loading={loading} />
    </form>
  );
};
